package com.eladfinish.bible.contest.ui.components.toolbar

import androidx.annotation.VisibleForTesting
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.displayCutout
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.systemBars
import androidx.compose.foundation.layout.union
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.material3.ButtonDefaults.buttonColors
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.Immutable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.colorPrimaryDark
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.common.gender.personalizeGender
import com.eladfinish.bible.contest.ui.components.buttons.MyIconButton
import com.eladfinish.bible.contest.ui.components.buttons.ToolbarTextButton
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.ext.isVisible
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarActionConfig
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarActionPlacement
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarHeaderComponentConfig
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarIcon
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarNavigationButtonConfig
import com.eladfinish.bible.contest.ui.components.toolbar.ui.ToolbarOverflowMenu
import com.eladfinish.bible.contest.ui.extensions.conditional
import com.eladfinish.bible.contest.ui.utils.automationTag
import dev.icerock.moko.resources.compose.colorResource
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.desc.desc

@VisibleForTesting(otherwise = VisibleForTesting.PRIVATE)
@Immutable
data class ToolbarPreviewOverrides<TId : Enum<TId>>(
    val overflowExpanded: Boolean = false,
    val activeSubMenuId: TId? = null,
)

@Composable
fun <TId : Enum<TId>> ConfigurableToolbar(
    configuration: ToolbarConfiguration<TId>,
    hebrewGender: HebrewGender,
    modifier: Modifier = Modifier,
    isCenterTitle: Boolean = false,
    applyStatusBarPadding: Boolean = true,
    isDebug: Boolean = false,
    @VisibleForTesting(otherwise = VisibleForTesting.NONE)
    previewOverrides: ToolbarPreviewOverrides<TId>? = null,
) = with(configuration) {
    AppToolbar(
        title = {
            Column {
                ToolbarHeader(
                    title = title,
                    subtitle = subtitle,
                    isCenterTitle = isCenterTitle,
                )
            }
        },
        isCenterTitle = isCenterTitle,
        navigationIcon = navigationButton?.composable(),
        actions = {
            val visibleActions = configuration.actions.filter { it.isVisible(isDebug = isDebug) }

            val outside = visibleActions.filter { it.placement == ToolbarActionPlacement.OnToolbar }
            val overflow = visibleActions.filter { it.placement == ToolbarActionPlacement.InOverflow }

            ToolbarActions(actions = outside, hebrewGender = hebrewGender)

            ToolbarOverflowMenu(
                overflowActions = overflow,
                hebrewGender = hebrewGender,
                initialExpanded = previewOverrides?.overflowExpanded == true,
                initialActiveSubMenuId = previewOverrides?.activeSubMenuId,
            )
        },
        showDivider = showDivider,
        modifier = modifier.conditional(applyStatusBarPadding) {
            background(colorResource(SharedRes.colors.colorPrimaryDark))
                .statusBarsPadding()
                // In landscape the navigation bar and the camera cutout sit at the side.
                .windowInsetsPadding(
                    WindowInsets.systemBars.union(WindowInsets.displayCutout).only(WindowInsetsSides.Horizontal)
                )
        },
    )
}


@Composable
private fun ToolbarHeader(
    title: ToolbarHeaderComponentConfig,
    subtitle: ToolbarHeaderComponentConfig?,
    isCenterTitle: Boolean,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        horizontalAlignment = if (isCenterTitle) Alignment.CenterHorizontally else Alignment.Start,
    ) {
        ToolbarTitle(title)

        ToolbarSubtitle(subtitle)
    }
}

@Composable
private fun ToolbarTitle(
    title: ToolbarHeaderComponentConfig,
) {
    when (title) {
        is ToolbarHeaderComponentConfig.CustomContent -> {
            title.content()
        }

        is ToolbarHeaderComponentConfig.Text -> {
            Text(
                modifier = Modifier.alpha(title.alpha),
                text = title.text,
                color = Color.White,
            )
        }
    }
}

@Composable
private fun ToolbarSubtitle(
    subtitle: ToolbarHeaderComponentConfig?,
) {
    when (subtitle) {
        is ToolbarHeaderComponentConfig.CustomContent -> {
            subtitle.content()
        }

        is ToolbarHeaderComponentConfig.Text -> {
            CompositionLocalProvider(
                LocalContentColor provides MaterialTheme.colorScheme.onSurfaceVariant,
                LocalTextStyle provides LocalTextStyle.current
                    .merge(MaterialTheme.typography.labelSmall)
            ) {
                Text(
                    modifier = Modifier.padding(top = 4.dp),
                    text = subtitle.text,
                )
            }
        }

        null -> Unit
    }
}


@Suppress("UnstableCollections")
@Composable
private fun <TId : Enum<TId>> ToolbarActions(
    actions: List<ToolbarActionConfig<TId>>,
    hebrewGender: HebrewGender,
) {
    actions.forEach {
        when (it) {
            is ToolbarActionConfig.IconAction -> ToolbarIconAction(
                action = it,
                modifier = Modifier.automationTag(it.testTag),
            )

            is ToolbarActionConfig.TextAction -> ToolbarTextAction(
                action = it,
                modifier = Modifier.automationTag(it.testTag),
            )

            is ToolbarActionConfig.TextActionRes -> ToolbarTextActionRes(
                action = it,
                hebrewGender = hebrewGender,
                modifier = Modifier.automationTag(it.testTag),
            )

            is ToolbarActionConfig.SubMenu<*> -> error("SubMenu is not supported on toolbar; it should be in overflow")
            is ToolbarActionConfig.CheckboxActionRes<*> -> error("CheckboxAction is not supported on toolbar; it should be in overflow")
        }
    }
}

@Composable
private fun <TId : Enum<TId>> ToolbarIconAction(
    action: ToolbarActionConfig.IconAction<TId>,
    modifier: Modifier = Modifier,
) {
    ToolbarIcon(
        icon = action.icon,
        contentDescription = action.contentDescription,
        iconColor = action.iconColor,
        onClick = action.onClick,
        enabled = action.enabled,
        modifier = modifier,
    )
}

@Composable
private fun <TId : Enum<TId>> ToolbarTextActionRes(
    action: ToolbarActionConfig.TextActionRes<TId>,
    hebrewGender: HebrewGender,
    modifier: Modifier = Modifier,
) {
    ToolbarTextButton(
        text = action.stringDesc.localized().personalizeGender(hebrewGender),
        onClick = action.onClick,
        enabled = action.enabled,
        modifier = modifier,
    )
}

@Composable
private fun <TId : Enum<TId>> ToolbarTextAction(
    action: ToolbarActionConfig.TextAction<TId>,
    modifier: Modifier = Modifier,
) {
    ToolbarTextButton(
        text = action.text,
        onClick = action.onClick,
        enabled = action.enabled,
        modifier = modifier,
        buttonColors = action.color?.let { buttonColors(contentColor = it) } ?: buttonColors(),
    )
}

@Composable
private fun ToolbarIcon(
    icon: ToolbarIcon,
    contentDescription: String?,
    iconColor: Color?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
) {
    ToolbarTooltipIfNeeded(
        tooltipText = contentDescription,
    ) {
        MyIconButton(
            onClick = onClick,
            modifier = modifier,
            enabled = enabled,
        ) {
            val iconSize = Modifier.size(24.dp)

            when (icon) {
                is ToolbarIcon.PainterIcon ->
                    Icon(
                        painter = icon.painter,
                        contentDescription = contentDescription,
                        modifier = iconSize,
                        tint = iconColor ?: MaterialTheme.colorScheme.onSurface,
                    )

                is ToolbarIcon.VectorIcon ->
                    Icon(
                        imageVector = icon.imageVector,
                        contentDescription = contentDescription,
                        modifier = iconSize,
                        tint = iconColor ?: MaterialTheme.colorScheme.onSurface,
                    )

                is ToolbarIcon.MokoImageIcon ->
                    Image(
                        painter = painterResource(icon.imageResource),
                        contentDescription = contentDescription,
                        // An explicit size: the image's own size is only 48dp on Android,
                        // where the density bucket is picked. Desktop and web load a single
                        // file and draw it pixel for pixel, which filled the whole toolbar.
                        modifier = Modifier.padding(start = 8.dp).size(48.dp),
                    )
            }
        }
    }
}

@Composable
private fun ToolbarNavigationButton(
    icon: ToolbarIcon,
    contentDescription: String?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    ToolbarIcon(
        icon = icon,
        contentDescription = contentDescription,
        iconColor = Color.White,
        onClick = onClick,
        modifier = modifier,
    )
}

internal fun ToolbarNavigationButtonConfig?.composable(): (@Composable () -> Unit) {
    val config = this ?: return {}

    return {
        when (config) {
            is ToolbarNavigationButtonConfig.Icon -> {
                ToolbarNavigationButton(
                    icon = config.icon,
                    contentDescription = config.contentDescription?.desc()?.localized(),
                    onClick = config.onClick,
                    modifier = Modifier.automationTag(config.testTag),
                )
            }

            is ToolbarNavigationButtonConfig.Text -> ToolbarTextButton(
                text = config.text,
                enabled = config.enabled,
                buttonColors = buttonColors(
                    contentColor = MaterialTheme.colorScheme.tertiaryContainer, // TODO: check
                    disabledContentColor = MaterialTheme.colorScheme.inverseSurface, // TODO: check
                ),
                modifier = Modifier.automationTag(testTag),
                onClick = onClick,
            )
        }
    }
}
